import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useEnhancedCart } from '@/contexts/EnhancedCartContext';
import { ArrowLeft, ShoppingBag, Truck, Shield, RotateCcw } from 'lucide-react';
import { formatPrice as formatPriceUtil } from '@/utils/currency';
import { CustomerAuthProvider } from '@/contexts/CustomerAuthContext';
import { EnhancedCartProvider } from '@/contexts/EnhancedCartContext';
import { ProductDetailLoader } from '@/components/storefront/StorefrontLoaders';
import api from '@/utils/api';
import { wholesaleConfigForCart, getEffectiveUnitPrice } from '@/utils/wholesalePricing';
import { applyStorefrontHead, storeCanonicalUrl, stripHtml } from '@/lib/storefrontHead';
import { trackSalesEvent } from '@/lib/sales-events';

interface ProductImage {
  url: string;
  alt?: string;
}

interface ProductVariant {
  sku: string;
  options: Record<string, string>;
  price: number;
  compareAtPrice?: number;
  inventory: number;
  wholesaleMinQuantity?: number;
  wholesaleTierPrice?: number;
}

interface ProductOption {
  name: string;
  values: Array<{
    name: string;
    price_adjustment: number;
  }>;
  required: boolean;
}

interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  compareAtPrice?: number;
  images: ProductImage[];
  category?: string;
  tags?: string[];
  options?: ProductOption[];
  variants?: ProductVariant[];
  status: string;
  wholesaleEnabled?: boolean;
  wholesaleMinQuantity?: number;
  wholesaleTierPrice?: number;
  seo?: {
    title?: string;
    description?: string;
    keywords?: string[] | string;
  };
}

interface StoreData {
  business: {
    storeName: string;
    url?: string;
    businessDescription?: string;
    currency: string;
    seo?: {
      title?: string;
      description?: string;
      keywords?: string;
      ogImage?: string;
    };
    settings: {
      logo?: string;
      colors?: {
        primary?: string;
        secondary?: string;
      };
      customization?: {
        template: string;
        hero?: {
          title: string;
          subtitle: string;
        };
        banner?: {
          enabled: boolean;
          imageUrl: string;
        };
        layoutOptions?: {
          showHero?: boolean;
          showSearch?: boolean;
          showCategories?: boolean;
        };
        announcements?: {
          enabled: boolean;
          text: string;
          backgroundColor: string;
          textColor: string;
        };
      };
    };
  };
  products: Product[];
}

export function ProductDetailPage() {
  const { storeUrl, productId } = useParams<{ storeUrl: string; productId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [store, setStore] = useState<StoreData | null>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);

  const { addToCart } = useEnhancedCart();

  useEffect(() => {
    if (!storeUrl || !product) return;
    trackSalesEvent(storeUrl, 'visit');
    trackSalesEvent(storeUrl, 'product');
  }, [storeUrl, product?.id]);

  useEffect(() => {
    const fetchStoreAndProduct = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await api.get(`/api/public/stores/${storeUrl}`);
        const data = response.data;

        // Normalize API payload to match TemplateInterface expectations
        const normalized: StoreData = {
          business: data.business,
          products: (data.products || []).map((p: any) => ({
            id: p._id || p.id,
            name: p.name,
            description: p.description,
            price: typeof p.basePrice === 'number' ? p.basePrice : (typeof p.price === 'number' ? p.price : 0),
            compareAtPrice: typeof p.compareAtPrice === 'number' ? p.compareAtPrice : undefined,
            images: Array.isArray(p.images) ? p.images.map((img: any) => ({ url: img.url, alt: img.alt })) : [],
            category: typeof p.category === 'object' && p.category ? p.category.name : p.category,
            tags: p.tags,
            options: p.options,
            wholesaleEnabled: Boolean(p.wholesaleEnabled),
            wholesaleMinQuantity: typeof p.wholesaleMinQuantity === 'number' ? p.wholesaleMinQuantity : undefined,
            wholesaleTierPrice: typeof p.wholesaleTierPrice === 'number' ? p.wholesaleTierPrice : undefined,
            variants: Array.isArray(p.variants) ? p.variants.map((v: any) => ({
              sku: v.sku,
              options: v.options instanceof Map ? Object.fromEntries(v.options) : (v.options || {}),
              price: v.price,
              compareAtPrice: v.compareAtPrice,
              inventory: v.inventory,
              wholesaleMinQuantity: typeof v.wholesaleMinQuantity === 'number' ? v.wholesaleMinQuantity : undefined,
              wholesaleTierPrice: typeof v.wholesaleTierPrice === 'number' ? v.wholesaleTierPrice : undefined,
            })) : undefined,
            status: p.status || 'active',
            seo: p.seo,
          })),
          services: (data.services || []).map((s: any) => ({
            id: s._id || s.id,
            name: s.name,
            description: s.description,
            price: s.price,
            duration: s.duration,
            image: s.image,
            category: s.category,
            status: s.status || 'active',
          })),
        };

        setStore(normalized);
        
        // Find the specific product
        const foundProduct = normalized.products.find(p => p.id === productId);
        if (foundProduct) {
          setProduct(foundProduct);
          
          // Set default selected options if product has options
          if (foundProduct.options) {
            const defaultOptions: Record<string, string> = {};
            foundProduct.options.forEach(option => {
              if (option.required && option.values.length > 0) {
                defaultOptions[option.name] = option.values[0].name;
              }
            });
            setSelectedOptions(defaultOptions);
          }
          
          // Find matching variant based on selected options
          if (foundProduct.variants && foundProduct.options) {
            const matchingVariant = foundProduct.variants.find(variant => {
              return foundProduct.options!.every(option => 
                variant.options[option.name] === selectedOptions[option.name]
              );
            });
            setSelectedVariant(matchingVariant || null);
          }
        } else {
          setError('Product not found');
        }
      } catch (err: any) {
        console.error('Error fetching store or product:', err);
        setError(err.response?.data?.message || 'Product not found');
      } finally {
        setIsLoading(false);
      }
    };

    if (storeUrl && productId) {
      fetchStoreAndProduct();
    }
  }, [storeUrl, productId, selectedOptions]);

  useEffect(() => {
    if (!store || !product || !storeUrl) return;

    const storeName = store.business.storeName;
    const productSeo = product.seo || {};
    const title =
      stripHtml(productSeo.title) || `${product.name} | ${storeName}`;
    const description =
      stripHtml(productSeo.description) ||
      stripHtml(product.description) ||
      `Buy ${product.name} from ${storeName}.`;
    const keywords = Array.isArray(productSeo.keywords)
      ? productSeo.keywords.filter(Boolean).join(', ')
      : productSeo.keywords || `${product.name}, ${storeName}`;
    const ogImage =
      product.images[0]?.url ||
      store.business.seo?.ogImage ||
      store.business.settings.logo ||
      '';
    const canonicalUrl = `${storeCanonicalUrl(store.business.url || storeUrl)}/product/${product.id}`;

    applyStorefrontHead({
      title,
      description,
      keywords,
      author: storeName,
      canonicalUrl,
      ogImage,
      logo: store.business.settings.logo,
      siteName: storeName,
      type: 'product',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description,
        image: ogImage || undefined,
        url: canonicalUrl,
        brand: { '@type': 'Brand', name: storeName },
        offers: {
          '@type': 'Offer',
          url: canonicalUrl,
          priceCurrency: store.business.currency || 'NGN',
          price: product.price,
          availability: 'https://schema.org/InStock',
          seller: {
            '@type': 'Organization',
            name: storeName,
            url: storeCanonicalUrl(store.business.url || storeUrl),
          },
        },
      },
    });
  }, [store, product, storeUrl]);

  const formatPrice = (price: number) =>
    formatPriceUtil(price, { currency: store?.business.currency || 'USD' });

  const handleAddToCart = () => {
    if (!product) return;

    const finalPrice = selectedVariant ? selectedVariant.price : product.price;
    const finalImage = product.images[selectedImageIndex] || product.images[0];

    // Create a product object with the final price and image
    const productToAdd = {
      ...product,
      price: finalPrice,
      images: finalImage ? [{ url: finalImage.url, alt: finalImage.alt }] : product.images,
    };

    addToCart(
      productToAdd, 
      quantity, 
      selectedOptions, 
      selectedVariant?.sku,
      selectedVariant ? `${Object.entries(selectedVariant.options).map(([key, value]) => `${key}: ${value}`).join(', ')}` : undefined,
      selectedVariant?.options
    );
    trackSalesEvent(storeUrl, 'cart');

    // Navigate to cart or show success message
    navigate(`/${storeUrl}`);
  };

  const handleOptionChange = (optionName: string, value: string) => {
    const newOptions = { ...selectedOptions, [optionName]: value };
    setSelectedOptions(newOptions);
  };

  if (isLoading) {
    return <ProductDetailLoader />;
  }

  if (error || !store || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center px-4">
          <h1 className="text-3xl font-bold text-destructive">Product Not Found</h1>
          <p className="text-muted-foreground">
            The product you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate(`/${storeUrl}`)}>
            Back to Store
          </Button>
        </div>
      </div>
    );
  }

  const theme = {
    primaryColor: store.business.settings.colors?.primary || '#3b82f6',
    secondaryColor: store.business.settings.colors?.secondary || '#10b981',
  };

  const currentPrice = selectedVariant ? selectedVariant.price : product.price;
  const currentComparePrice = selectedVariant ? selectedVariant.compareAtPrice : product.compareAtPrice;
  const wsConfig = wholesaleConfigForCart(product, selectedVariant?.sku);
  const effectiveUnit = getEffectiveUnitPrice(currentPrice, quantity, wsConfig);
  const wholesaleActive = Boolean(wsConfig && quantity >= wsConfig.minQuantity);

  return (
    <CustomerAuthProvider storeUrl={storeUrl || ''}>
      <EnhancedCartProvider storeUrl={storeUrl}>
          <div className="min-h-screen bg-white">
            <header className="sticky top-0 z-40 border-b border-black/5 bg-white/90 backdrop-blur-md">
              <div className="mx-auto flex h-14 max-w-[96%] items-center gap-3 sm:h-16 lg:max-w-[80%]">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/${storeUrl}`)}
                  className="rounded-full text-black/60 hover:text-black"
                >
                  <ArrowLeft className="mr-1.5 h-4 w-4" />
                  Back
                </Button>

                <div className="ml-auto flex min-w-0 items-center gap-2.5">
                  {store.business.settings.logo && (
                    <img
                      src={store.business.settings.logo}
                      alt={store.business.storeName}
                      className="size-8 rounded-full object-cover ring-1 ring-black/5"
                    />
                  )}
                  <h1
                    className="truncate font-heading text-sm font-bold sm:text-base"
                    style={{ color: theme.primaryColor }}
                  >
                    {store.business.storeName}
                  </h1>
                </div>
              </div>
            </header>

            <main className="mx-auto max-w-[96%] px-4 py-8 sm:py-12 lg:max-w-[80%]">
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
                <div className="space-y-4">
                  <div className="aspect-square overflow-hidden rounded-[1.75rem] bg-black/[0.03] ring-1 ring-black/5">
                    {product.images.length > 0 ? (
                      <img
                        src={product.images[selectedImageIndex]?.url}
                        alt={product.images[selectedImageIndex]?.alt || product.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <span className="text-black/30">No image available</span>
                      </div>
                    )}
                  </div>

                  {product.images.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {product.images.map((image, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setSelectedImageIndex(index)}
                          className="size-20 shrink-0 overflow-hidden rounded-2xl border-2 transition-colors"
                          style={{
                            borderColor:
                              index === selectedImageIndex
                                ? theme.primaryColor
                                : "rgba(0,0,0,0.08)",
                          }}
                        >
                          <img
                            src={image.url}
                            alt={image.alt || product.name}
                            className="h-full w-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-6">
                  <div>
                    {product.category && (
                      <p
                        className="mb-2 font-heading text-xs font-semibold uppercase tracking-[0.18em]"
                        style={{ color: `${theme.primaryColor}99` }}
                      >
                        {product.category}
                      </p>
                    )}
                    <h1 className="font-display text-3xl font-black tracking-tight text-black sm:text-4xl">
                      {product.name}
                    </h1>

                    <div className="mt-4 space-y-2">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="text-sm text-black/45">Retail (each)</span>
                        <span
                          className="text-3xl font-bold"
                          style={{ color: theme.primaryColor }}
                        >
                          {formatPrice(currentPrice)}
                        </span>
                        {currentComparePrice && currentComparePrice > currentPrice && (
                          <span className="text-xl text-black/30 line-through">
                            {formatPrice(currentComparePrice)}
                          </span>
                        )}
                      </div>
                      {wsConfig && (
                        <p className="rounded-2xl bg-black/[0.03] px-4 py-3 text-sm leading-relaxed text-black/70">
                          Wholesale: <strong>{formatPrice(wsConfig.tierPrice)}</strong> for{" "}
                          <strong>{wsConfig.minQuantity}</strong>+ units (
                          {formatPrice(wsConfig.tierPrice / wsConfig.minQuantity)} / unit).{" "}
                          {wholesaleActive ? (
                            <span className="font-medium text-emerald-700">
                              Your unit price: {formatPrice(effectiveUnit)}
                            </span>
                          ) : (
                            <span>
                              Add {Math.max(0, wsConfig.minQuantity - quantity)} more for
                              wholesale unit pricing.
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>

                  {product.description && (
                    <div>
                      <h3 className="mb-2 font-heading text-base font-bold">Description</h3>
                      <p className="leading-relaxed text-black/55">{product.description}</p>
                    </div>
                  )}

                  {product.options && product.options.length > 0 && (
                    <div className="space-y-4">
                      {product.options.map((option) => (
                        <div key={option.name}>
                          <label className="mb-2 block text-sm font-semibold">
                            {option.name}{" "}
                            {option.required && <span className="text-red-500">*</span>}
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {option.values.map((value) => {
                              const selected =
                                selectedOptions[option.name] === value.name;
                              return (
                                <button
                                  key={value.name}
                                  type="button"
                                  onClick={() =>
                                    handleOptionChange(option.name, value.name)
                                  }
                                  className="rounded-full border border-black/10 px-4 py-2 text-sm font-medium text-black/70 transition-colors hover:border-black/20"
                                  style={
                                    selected
                                      ? {
                                          borderColor: theme.primaryColor,
                                          backgroundColor: `${theme.primaryColor}14`,
                                          color: theme.primaryColor,
                                        }
                                      : undefined
                                  }
                                >
                                  {value.name}
                                  {value.price_adjustment > 0 && (
                                    <span className="ml-1 text-xs">
                                      (+{formatPrice(value.price_adjustment)})
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div>
                    <label className="mb-2 block text-sm font-semibold">Quantity</label>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="size-10 rounded-full"
                      >
                        -
                      </Button>
                      <span className="w-12 text-center font-semibold">{quantity}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setQuantity(quantity + 1)}
                        className="size-10 rounded-full"
                      >
                        +
                      </Button>
                    </div>
                  </div>

                  <Button
                    onClick={handleAddToCart}
                    className="h-12 w-full rounded-full text-base font-semibold text-white"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    <ShoppingBag className="mr-2 h-5 w-5" />
                    Add to Cart
                  </Button>

                  <div className="grid grid-cols-1 gap-3 border-t border-black/5 pt-6 sm:grid-cols-3">
                    <div className="flex items-center gap-2 text-sm text-black/50">
                      <Truck className="h-4 w-4" />
                      <span>Free shipping</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-black/50">
                      <Shield className="h-4 w-4" />
                      <span>Secure payment</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-black/50">
                      <RotateCcw className="h-4 w-4" />
                      <span>Easy returns</span>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </EnhancedCartProvider>
    </CustomerAuthProvider>
  );
}
